import React, { useState, useEffect, useRef } from 'react';
import { db } from '../config/firebase';
import {
  collection, addDoc, getDocs, query, where, doc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import Sidebar from './Shared/Sidebar';
import Icon from './Shared/Icons';
import { uploadCertificateToCloudinary } from '../config/cloudinary';
import { resolveAuthorName } from '../utils/programData';
import { fmtDateTime } from '../utils/weekUtils';
import { S, T } from './Programs/ui';

// ══════════════════════════════════════════════════════════════════════
//  AKAUNTI YANGU — Ripoti Binafsi
//  Sehemu ya kila mfanyakazi kuweka ripoti yoyote — ya tukio, ya kazi
//  maalum, au kitu chochote kisichohusiana moja kwa moja na programu.
//  Lengo ni kuitunza tu, na admin kuiona. Si sehemu ya ripoti za wiki
//  za programu — hiyo inabaki kama ilivyo.
// ══════════════════════════════════════════════════════════════════════

const COLLECTION = 'personalReports';

export default function MyAccount() {
  const { currentUser, userRole } = useAuth();
  const fileRef = useRef(null);

  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');
  const [reports, setReports] = useState([]);
  const [allReports, setAllReports] = useState([]);
  const [tab, setTab] = useState('mine');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function load() {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, COLLECTION), where('authorId', '==', currentUser.uid)));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setReports(list);

      if (userRole === 'admin') {
        const allSnap = await getDocs(collection(db, COLLECTION));
        setAllReports(
          allSnap.docs.map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
        );
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function submit() {
    if (!title.trim()) { setErr('Weka kichwa cha ripoti.'); return; }
    setUploading(true); setErr(''); setMsg('');
    try {
      const name = await resolveAuthorName(currentUser);
      let fileUrl = '', fileName = '';
      if (file) {
        setProgress('Inapakia faili...');
        fileUrl = await uploadCertificateToCloudinary(file);
        fileName = file.name;
      }
      await addDoc(collection(db, COLLECTION), {
        title: title.trim(),
        notes: notes.trim(),
        fileUrl, fileName,
        authorId: currentUser.uid,
        authorName: name,
        authorRole: userRole || '',
        createdAt: serverTimestamp(),
      });
      setMsg('Ripoti imehifadhiwa.');
      setTitle(''); setNotes(''); setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      load();
      setTimeout(() => setMsg(''), 3500);
    } catch (e) {
      console.error(e);
      setErr('Imeshindikana kuhifadhi. Angalia mtandao kisha jaribu tena.');
    }
    setUploading(false); setProgress('');
  }

  async function remove(id) {
    if (!window.confirm('Futa ripoti hii?')) return;
    try { await deleteDoc(doc(db, COLLECTION, id)); load(); }
    catch (e) { console.error(e); setErr('Imeshindikana kufuta.'); }
  }

  return (
    <div style={S.layout}>
      <Sidebar />
      <div style={S.main}>

        <h1 style={S.h1}>Akaunti Yangu</h1>
        <p style={{ ...S.sub, marginTop: '6px', marginBottom: '18px' }}>
          {currentUser?.email} · {userRole === 'admin' ? 'Admin' : 'Staff'}
        </p>

        <div style={S.card}>
          <h2 style={S.h2}>Weka Ripoti Binafsi</h2>
          <div style={{ fontSize: '12px', color: T.muted, marginBottom: '16px', lineHeight: 1.7 }}>
            Ripoti ya tukio, kazi maalum, au kitu chochote kisichohusiana na
            ripoti za wiki za programu. Inahifadhiwa hapa na admin anaweza
            kuiona wakati wowote.
          </div>

          {msg && <div style={S.ok}>{msg}</div>}
          {err && <div style={S.err}>{err}</div>}

          <label style={S.label}>Kichwa cha ripoti *</label>
          <input style={{ ...S.input, marginBottom: '14px' }} value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="mf. Ripoti ya ziara ya shule ya Kaloleni" />

          <label style={S.label}>Maelezo</label>
          <textarea style={{ ...S.input, minHeight: '110px', marginBottom: '14px', resize: 'vertical' }}
            value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Andika ripoti yako hapa, au eleza faili ulilopakia..." />

          <label style={S.label}>Faili (hiari — PDF, picha, Word)</label>
          <input
            ref={fileRef}
            type="file"
            onChange={e => setFile(e.target.files?.[0] || null)}
            style={{ ...S.input, padding: '9px 13px', marginBottom: '18px' }}
          />

          <button style={{ ...S.btn, opacity: uploading ? 0.6 : 1 }} disabled={uploading} onClick={submit}>
            {uploading ? (progress || 'Inahifadhi...') : 'Hifadhi Ripoti'}
          </button>
        </div>

        {userRole === 'admin' && (
          <div style={{ display: 'flex', gap: '7px', marginBottom: '14px' }}>
            {[
              { k: 'mine', l: `Zangu (${reports.length})` },
              { k: 'all', l: `Za Staff Wote (${allReports.length})` },
            ].map(t => (
              <button key={t.k} onClick={() => setTab(t.k)} style={{
                padding: '9px 16px', borderRadius: '99px', cursor: 'pointer',
                border: `1px solid ${tab === t.k ? T.green : T.line}`,
                background: tab === t.k ? T.green : '#fff',
                color: tab === t.k ? '#fff' : T.muted,
                fontSize: '12.5px', fontWeight: 700, fontFamily: 'inherit',
              }}>{t.l}</button>
            ))}
          </div>
        )}

        <h2 style={{ ...S.h2, marginTop: '8px' }}>
          {tab === 'all' ? `Ripoti za Staff Wote (${allReports.length})` : `Ripoti Zangu (${reports.length})`}
        </h2>
        {loading ? (
          <div style={{ ...S.card, ...S.empty }}>Inapakia...</div>
        ) : (tab === 'all' ? allReports : reports).length === 0 ? (
          <div style={{ ...S.card, ...S.empty }}>
            {tab === 'all' ? 'Bado hakuna ripoti kutoka kwa staff.' : 'Bado hujaweka ripoti yoyote.'}
          </div>
        ) : (tab === 'all' ? allReports : reports).map(r => (
          <div key={r.id} style={S.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {tab === 'all' && (
                  <div style={{ ...S.pill, color: T.green, background: `${T.green}12`, marginBottom: '8px' }}>
                    {r.authorName || 'Staff'}
                  </div>
                )}
                <div style={{ fontSize: '14px', fontWeight: 700, color: T.ink }}>{r.title}</div>
                {r.notes && (
                  <div style={{ fontSize: '12.5px', color: T.muted, marginTop: '6px', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {r.notes}
                  </div>
                )}
                {r.fileUrl && (
                  <a href={r.fileUrl} target="_blank" rel="noreferrer" style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '9px',
                    fontSize: '11.5px', fontWeight: 700, color: T.green, textDecoration: 'none',
                  }}>
                    <Icon name="link" size={12} /> {r.fileName || 'Fungua faili'}
                  </a>
                )}
                <div style={{ fontSize: '11px', color: T.muted, marginTop: '9px' }}>
                  {fmtDateTime(r.createdAt)}
                </div>
              </div>
              {tab === 'mine' && (
                <button onClick={() => remove(r.id)} style={{ ...S.btnGhost, padding: '7px 12px', fontSize: '11px', color: '#DC2626', alignSelf: 'flex-start' }}>
                  Futa
                </button>
              )}
            </div>
          </div>
        ))}

      </div>
    </div>
  );
}
